import { ReactElement, SyntheticEvent, useEffect, useState } from 'react'
import {
  Input,
  FlexWrapper,
  EditableDropdown,
  PageWrapper,
  ActionButton,
  Loader,
  Button,
  SectionTitle
} from '../../../../components'
import { useHistory } from 'react-router-dom'
import strings from '../../../../locale/en'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import {
  getAvailableCourses,
  getInstitutes,
  getWebexUsers,
  resetBranch,
  saveBranch,
  editBranch
} from '../../../../redux/coachingCenter/actions'
import { status as statusList, yesOrNO } from '../../../../const/dropdown'
import {
  getCoursesDropdown,
  getInstituteDropdown,
  getWebexUsersDropdown
} from '../../../../helpers'
import validateBranchForm from './helpers'
import { BranchField } from '../typings'
import { Branch } from '../../../../redux/coachingCenter/types'
import { hasFormError } from '../../../../helpers/formValidation'
import { DropdownListProps } from '../../../../components/EditableDropdown/typings'
import ROUTES from '../../../../const/routes'
import { ROLE } from '../../../../helpers/determineMenu'

const AddBranch = (): ReactElement => {
  const {
    isLoading,
    institutes,
    courses,
    webexUsers,
    branch,
    userDetail,
    userRole
  } = useSelector(
    (state: RootState) => ({
      isLoading: state.coachingCenter.isLoading,
      institutes: state.coachingCenter.institutes,
      courses: state.coachingCenter.availableCourses,
      webexUsers: state.coachingCenter.webexUsers,
      branch: state.coachingCenter.selectedBranch as Branch,
      userDetail: state.user?.userInfo?.userDetail,
      userRole: state.user.userInfo?.role
    }),
    shallowEqual
  )

  const dispatch = useDispatch()
  const history = useHistory()
  const isEdit = history.location.pathname !== ROUTES.BRANCH_ADD
  const {
    branchDetails: {
      instituteName,
      branchName,
      selectWebexUser,
      address1,
      address2,
      country,
      state,
      city,
      zipcode,
      mobileNumber,
      email,
      mainBranch,
      questionLimit,
      availableCourses,
      status,
      placeholders: { selectCoachingCenter, selectOption }
    },
    menu: {
      admin: { addCoachingCenterBranch, editCoachingCenterBranch }
    },
    button: { save, back }
  } = strings

  const institutesDropdown = getInstituteDropdown(institutes)
  const availableCoursesDropdown = getCoursesDropdown(courses)
  const webexUsersDropdown = getWebexUsersDropdown(webexUsers)

  const [values, setValues] = useState(branch || {})
  const [errors, setErrors] = useState({} as Record<string, string>)
  const defaultInstituteId = userDetail?.coachingCenterId || ''

  const validateField = (field: BranchField): void => {
    setErrors(
      validateBranchForm({
        values,
        errors,
        field
      })
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    const validationError = validateBranchForm({
      values,
      errors
    })

    if (hasFormError(validationError)) {
      setErrors(validationError)
    } else {
      isEdit ? dispatch(editBranch(values)) : dispatch(saveBranch(values))
    }
  }

  useEffect(() => {
    if (userRole === ROLE.INSTITUTE_ADMIN) {
      setValues({
        ...values,
        coachingCenterId: defaultInstituteId
      })
      dispatch(getAvailableCourses(defaultInstituteId))
    } else {
      dispatch(getInstitutes())
    }

    dispatch(getWebexUsers())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    !isEdit && dispatch(resetBranch())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  useEffect(() => {
    setValues(branch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch])

  return isLoading ? (
    <Loader />
  ) : (
    <PageWrapper id="container">
      <SectionTitle
        title={isEdit ? editCoachingCenterBranch : addCoachingCenterBranch}
        hasBorder
      />
      <FlexWrapper justifyContent="space-around">
        {userRole !== ROLE.INSTITUTE_ADMIN && (
          <EditableDropdown
            width="30%"
            title={instituteName}
            isRequired
            dropdownList={institutesDropdown}
            handleSelect={(institue) => {
              setValues({
                ...values,
                coachingCenterId: institue?.id,
                questionLimit: '',
                courseIds: []
                //maxQuestionLimit: institue?.questionLimit
              })
              dispatch(getAvailableCourses(institue?.id))
              setErrors(
                validateBranchForm({
                  values: { ...values, coachingCenterId: institue?.id },
                  errors,
                  field: 'instituteName'
                })
              )
            }}
            onBlur={() => validateField('instituteName')}
            error={errors.instituteName}
            placeholder={selectCoachingCenter}
            defaultValue={{
              id: values?.coachingCenterId || '',
              name: values?.coachingCenterId
                ? institutesDropdown.filter(
                    (institue) => institue?.id === values?.coachingCenterId
                  )[0]?.name
                : ''
            }}
          />
        )}
        <Input
          label={branchName}
          isRequired
          placeholder={branchName}
          value={values?.branchName || ''}
          width="30%"
          name="branchName"
          onBlur={() => validateField('branchName')}
          error={errors.branchName}
          onChange={(value: string) =>
            setValues({ ...values, branchName: value })
          }
        />
        <EditableDropdown
          width="30%"
          title={selectWebexUser}
          dropdownList={webexUsersDropdown}
          placeholder={selectWebexUser}
          isMultiChoice
          handleMultiSelect={(items) => {
            const ids = items.map((item: DropdownListProps) => item.id)

            setValues({ ...values, webexUserIds: ids })
          }}
        />
        <Input
          label={address1}
          isRequired
          placeholder={address1}
          value={values?.address1 || ''}
          name="address1"
          onBlur={() => validateField('address1')}
          error={errors.address1}
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, address1: value })
          }
        />
        <Input
          label={address2}
          isRequired
          placeholder={address2}
          value={values?.address2 || ''}
          name="address2"
          onBlur={() => validateField('address2')}
          error={errors.address2}
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, address2: value })
          }
        />
        <Input
          label={country}
          isRequired
          placeholder={country}
          value={values?.country || ''}
          name="country"
          onBlur={() => validateField('country')}
          error={errors.country}
          width="30%"
          onChange={(value: string) => setValues({ ...values, country: value })}
        />
        <Input
          label={state}
          isRequired
          placeholder={state}
          value={values?.state || ''}
          name="state"
          onBlur={() => validateField('state')}
          error={errors.state}
          width="30%"
          onChange={(value: string) => setValues({ ...values, state: value })}
        />
        <Input
          label={city}
          isRequired
          placeholder={city}
          value={values?.city || ''}
          name="city"
          onBlur={() => validateField('city')}
          error={errors.city}
          width="30%"
          onChange={(value: string) => setValues({ ...values, city: value })}
        />
        <Input
          label={zipcode}
          isRequired
          inputType="number"
          placeholder={zipcode}
          value={values?.zipCode || ''}
          name="zipCode"
          onBlur={() => validateField('zipCode')}
          error={errors.zipCode}
          width="30%"
          onChange={(value: string) => setValues({ ...values, zipCode: value })}
        />
        <Input
          label={mobileNumber}
          isRequired
          inputType="number"
          placeholder={mobileNumber}
          value={values?.mobileNumber || ''}
          name="mobileNumber"
          onBlur={() => validateField('mobileNumber')}
          error={errors.mobileNumber}
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, mobileNumber: value })
          }
        />
        <Input
          label={email}
          isRequired
          placeholder={email}
          value={values?.email || ''}
          name="email"
          onBlur={() => validateField('email')}
          error={errors.email}
          width="30%"
          onChange={(value: string) => setValues({ ...values, email: value })}
        />
        <EditableDropdown
          width="30%"
          title={mainBranch}
          isRequired
          dropdownList={yesOrNO}
          placeholder={selectOption}
          handleSelect={(selectedOption) => {
            setValues({ ...values, isMainBranch: selectedOption.name })
            setErrors(
              validateBranchForm({
                values: { ...values, isMainBranch: selectedOption.name },
                errors,
                field: 'mainBranch'
              })
            )
          }}
          onBlur={() => validateField('mainBranch')}
          error={errors.mainBranch}
          defaultValue={{
            id: values?.isMainBranch ? '01' : '02',
            name: values?.isMainBranch ? 'YES' : 'NO'
          }}
        />
        <Input
          label={questionLimit}
          isRequired
          value={values?.questionLimit || ''}
          inputType="number"
          placeholder={questionLimit}
          name="questionLimit"
          width="30%"
          onBlur={() => validateField('questionLimit')}
          error={errors.questionLimit}
          onChange={(value: string) =>
            setValues({ ...values, questionLimit: value })
          }
        />
        <EditableDropdown
          width="30%"
          title={availableCourses}
          isRequired
          dropdownList={availableCoursesDropdown}
          placeholder={availableCourses}
          isMultiChoice
          handleMultiSelect={(items) => {
            const ids = items.map((item: DropdownListProps) => item.id)
            setValues({ ...values, courseIds: ids })
            setErrors(
              validateBranchForm({
                values: { ...values, courseIds: ids },
                errors,
                field: 'availableCourses'
              })
            )
          }}
          onBlur={() => validateField('availableCourses')}
          error={errors.availableCourses}
        />
        <EditableDropdown
          width="30%"
          title={status}
          isRequired
          dropdownList={statusList}
          placeholder={selectOption}
          handleSelect={(selectedOption) => {
            setValues({ ...values, status: selectedOption.name })
            setErrors(
              validateBranchForm({
                values: { ...values, status: selectedOption.name },
                errors,
                field: 'status'
              })
            )
          }}
          onBlur={() => validateField('status')}
          error={errors.status}
          defaultValue={{
            id: values?.status === 'ACTIVE' ? '01' : '02',
            name: values?.status
          }}
        />
      </FlexWrapper>
      <FlexWrapper justifyContent="center">
        <ActionButton
          variant="outline-secondary"
          onClick={() => {
            dispatch(resetBranch())
            history.goBack()
          }}
        >
          {back}
        </ActionButton>
        <Button
          onClick={(e: SyntheticEvent) => {
            dispatch(resetBranch())
            handleSubmit(e)
          }}
        >
          {save}
        </Button>
      </FlexWrapper>
    </PageWrapper>
  )
}

export default AddBranch
